import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, MapPin, Phone, Clock } from 'lucide-react';
import { ANPC, COMPANY, DEVELOPER, CONTACT, SOCIAL } from '../constants/site';
import { useLanguage } from '../i18n/LanguageContext';

export const Footer = () => {
  const { t } = useLanguage();
  const links = [
    { to: '/tamplarie-pvc', label: t('nav.pvc') },
    { to: '/rulouri', label: t('nav.rulouri') },
    { to: '/porti-de-garaj', label: t('nav.garaj') },
    { to: '/automatizari', label: t('nav.auto') },
    { to: '/portofoliu', label: t('nav.portofoliu') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer data-testid="footer" className="border-t border-line/10 bg-surface2">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="mb-6">
            <img src="/logo.png" alt="Domidoor Solutions" className="h-16 w-auto logo-img logo-img-light" />
            <img src="/logo-white.png" alt="Domidoor Solutions" className="h-16 w-auto logo-img logo-img-dark" />
          </div>
          <p className="text-smoke text-sm leading-relaxed max-w-xs">{t('footer.tagline')}</p>
          <div className="flex items-center gap-3 mt-6">
            <a
              href={SOCIAL.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook"
              data-testid="footer-social-facebook"
              className="w-10 h-10 grid place-items-center rounded-full border border-line/15 text-smoke hover:text-accent hover:border-accent/50 hover:-translate-y-0.5 transition-[color,border-color,transform] duration-300"
            >
              <Facebook size={16} strokeWidth={1.8} />
            </a>
            <a
              href={SOCIAL.instagram}
              target="_blank"
              rel="noreferrer"
              aria-label="Instagram"
              data-testid="footer-social-instagram"
              className="w-10 h-10 grid place-items-center rounded-full border border-line/15 text-smoke hover:text-accent hover:border-accent/50 hover:-translate-y-0.5 transition-[color,border-color,transform] duration-300"
            >
              <Instagram size={16} strokeWidth={1.8} />
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent mb-6">{t('footer.menu')}</p>
          <ul className="space-y-3">
            {links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-sm text-smoke hover:text-ink transition-colors">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent mb-6">{t('footer.contact')}</p>
          <ul className="space-y-4 text-sm text-smoke">
            <li>
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-start gap-3 hover:text-ink transition-colors">
                <Phone size={15} className="mt-0.5 text-accent shrink-0" /> {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-start gap-3 hover:text-ink transition-colors break-all">
                <Mail size={15} className="mt-0.5 text-accent shrink-0" /> {CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={15} className="mt-0.5 text-accent shrink-0" /> {CONTACT.address}
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent mb-6">{t('footer.program')}</p>
          <p className="flex items-center gap-3 text-sm text-smoke">
            <Clock size={15} className="text-accent" /> {t('footer.hours')}
          </p>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-whatsapp"
            className="inline-flex mt-6 border border-line/20 text-ink text-xs font-bold uppercase tracking-[0.15em] px-6 py-3 hover:border-[#25D366] hover:text-[#25D366] transition-colors duration-300"
          >
            WhatsApp
          </a>
        </div>
      </div>
      <div className="border-t border-line/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-accent shrink-0">{t('footer.zones')}</span>
          <Link to="/porti-de-garaj-timisoara" data-testid="footer-zone-garaj-timisoara" className="text-xs text-smoke hover:text-ink transition-colors">{t('localTm.title')}</Link>
          <Link to="/tamplarie-pvc-timisoara" data-testid="footer-zone-pvc-timisoara" className="text-xs text-smoke hover:text-ink transition-colors">{t('localPvcTm.title')}</Link>
          <Link to="/rulouri-timisoara" data-testid="footer-zone-rulouri-timisoara" className="text-xs text-smoke hover:text-ink transition-colors">{t('localRulouriTm.title')}</Link>
          <Link to="/porti-de-garaj-arad" data-testid="footer-zone-garaj-arad" className="text-xs text-smoke hover:text-ink transition-colors">{t('localGarajArad.title')}</Link>
          <Link to="/tamplarie-pvc-arad" data-testid="footer-zone-pvc-arad" className="text-xs text-smoke hover:text-ink transition-colors">{t('localPvcArad.title')}</Link>
          <Link to="/rulouri-arad" data-testid="footer-zone-rulouri-arad" className="text-xs text-smoke hover:text-ink transition-colors">{t('localRulouriArad.title')}</Link>
        </div>
      </div>
      <div className="border-t border-line/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-6 flex flex-wrap items-center gap-x-7 gap-y-3">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-accent">{t('legal.title')}</span>
          <Link to="/politica-confidentialitate" data-testid="footer-privacy-link" className="text-sm text-smoke hover:text-ink transition-colors">{t('legal.privacy')}</Link>
          <Link to="/politica-cookies" data-testid="footer-cookies-link" className="text-sm text-smoke hover:text-ink transition-colors">{t('legal.cookies')}</Link>
          <a href={ANPC.sal} target="_blank" rel="noreferrer" data-testid="footer-anpc-sal" className="text-sm text-smoke hover:text-ink transition-colors">{t('legal.anpcSal')}</a>
          <a href={ANPC.sol} target="_blank" rel="noreferrer" data-testid="footer-anpc-sol" className="text-sm text-smoke hover:text-ink transition-colors">{t('legal.anpcSol')}</a>
        </div>
      </div>
      <div className="border-t border-line/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-6 pb-40 md:py-6 text-xs text-smoke/60">
          <p data-testid="footer-company" className="text-smoke/80 leading-relaxed mb-3">
            {COMPANY.name} · {t('footer.companyIds')} · {COMPANY.address}
          </p>
          <div className="flex flex-col md:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Domidoor Solutions. {t('footer.rights')}</span>
            <span>
              {t('footer.credit')}{' '}
              <a href={DEVELOPER.url} target="_blank" rel="noreferrer" data-testid="footer-developer-link" className="text-smoke hover:text-accent transition-colors underline-offset-2 hover:underline">
                {DEVELOPER.name}
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
